/* Render a ShareGrid to a PNG image suitable for clipboard / social
   share. Output mirrors the text grid one-for-one — header line, the
   same set of rows with the same per-cell coloring, footer line, and
   the optional Hard Mode line — but draws each filled square as a
   rounded rectangle on top of the PalinDragon monogram (filled
   edge-to-edge as the image background) so the image reads as a
   "pretty share card" rather than a screenshot of emoji.

   The card is always 1200×1200 with the monogram as its background;
   text is painted in a fixed near-white. */

import type { GridCell, ShareGrid, SquareMark } from "./share";

// ── Palette ────────────────────────────────────────────────────────────
//
// Saturated cell colors match the share emojis (🟩🟨⬛⬜🟧🟦) so the
// painted card reads the same as the emoji-grid text. Foreground (text,
// punctuation) is a near-white that contrasts against the dark blue of
// the monogram backdrop.

const PALETTE: Record<SquareMark, string> = {
  green: "#6aaa64",
  yellow: "#c9b458",
  red: "#d33a2c",
  gray: "#787c7e",
  placeholder: "#d3d6da",
  hint: "#f5a623",
  spaces: "#4189dd",
};

const FG = "#f5f5f5";

// ── Layout constants (CSS pixels — multiplied by devicePixelRatio at
//    paint time for crispness) ──────────────────────────────────────────

// Tiles live in a regular SLOT × SLOT grid (square cells, both axes).
// Each tile is drawn centered within its slot at CELL_FILL of the slot
// size, so CELL_FILL = 1.0 means tiles touch with no gap anywhere in
// the grid, and CELL_FILL = 0.1 means the visible tile is 10% of the
// slot in each dimension. Aspect ratio stays 1:1 always.
const SLOT = 36;
const CELL_FILL = 0.8;
const CELL = SLOT * CELL_FILL;
const TILE_INSET = (SLOT - CELL) / 2;
const CELL_RADIUS = 5 * CELL_FILL;
// Tiles paint at 80% opacity so the darkened monogram shows through them.
const CELL_ALPHA = 0.8;
const SPACE_W = 14;
const PUNCT_W = 22;
const SECTION_GAP = 18;

// Final image is always FIXED_SIZE × FIXED_SIZE CSS pixels with a 5%
// margin around the content (60px at 1200). Content is laid out at its
// natural size, then uniformly scaled so it fits within
// FIXED_SIZE − 2·MIN_MARGIN on both axes, then centered.
const FIXED_SIZE = 1200;
const MIN_MARGIN = FIXED_SIZE * 0.05;
const MAX_CONTENT = FIXED_SIZE - MIN_MARGIN * 2;

const FONT_STACK =
  '"Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, ' +
  '"Apple Color Emoji", "Segoe UI Emoji", system-ui, sans-serif';

// All text is sized at TEXT_SCALE × its prior baseline. The auto-fit
// pass shrinks the whole content block to honor MAX_CONTENT, so larger
// text shows up as a bigger text-to-tile ratio rather than spilling
// out of the card.
const TEXT_SCALE = 2;

const HEADER_FONT = `700 ${24 * TEXT_SCALE}px ${FONT_STACK}`;
const HEADER_LH = 28 * TEXT_SCALE;
const FOOTER_FONT = `600 ${16 * TEXT_SCALE}px ${FONT_STACK}`;
const FOOTER_LH = 20 * TEXT_SCALE;
const HARD_FONT = `700 ${30 * TEXT_SCALE}px ${FONT_STACK}`;
const HARD_LH = 36 * TEXT_SCALE;
const PUNCT_FONT = `700 ${22 * TEXT_SCALE}px ${FONT_STACK}`;
const URL_FONT = `500 ${12 * TEXT_SCALE}px ${FONT_STACK}`;
const URL_LH = 14 * TEXT_SCALE;
// Two-line bottom block: a call-to-action above the canonical URL.
// Rendered bottom-up so the URL is the line flush with the bottom margin.
const URL_LINES = ["Can you solve today’s challenge?", "PalinDragon.com"];

// ── Monogram backdrop ──────────────────────────────────────────────────
//
// Loaded once and cached for the lifetime of the page; subsequent
// renders reuse the bitmap. A failed fetch leaves the promise rejected
// — `renderShareImageBlob` catches and falls back to a flat dark fill so
// the share never silently produces an unreadable card.

const MONOGRAM_SRC = "/monogram.jpg";
const MONOGRAM_FALLBACK_BG = "#0d2540";

let monogramPromise: Promise<ImageBitmap> | null = null;
function loadMonogram(): Promise<ImageBitmap> {
  if (monogramPromise == null) {
    monogramPromise = fetch(MONOGRAM_SRC)
      .then((r) => {
        if (!r.ok) throw new Error(`monogram fetch ${r.status}`);
        return r.blob();
      })
      .then((b) => createImageBitmap(b));
  }
  return monogramPromise;
}

// ── Helpers ────────────────────────────────────────────────────────────

function rowWidth(row: readonly GridCell[]): number {
  if (row.length === 0) return 0;
  let w = 0;
  for (const c of row) {
    if (c.kind === "square") w += SLOT;
    else if (c.kind === "space") w += SPACE_W;
    else w += PUNCT_W;
  }
  return w;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}

/* Regular octagon inscribed in the (w × h) cell. The 0.293 inset is
   1 − 1/√2, matching the --clip-path-octagon CSS token used by the
   in-game .cell--red rule, so the in-game silhouette and the share
   image share one geometry. */
function octagon(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const ix = w * 0.293;
  const iy = h * 0.293;
  ctx.beginPath();
  ctx.moveTo(x + ix,     y);
  ctx.lineTo(x + w - ix, y);
  ctx.lineTo(x + w,      y + iy);
  ctx.lineTo(x + w,      y + h - iy);
  ctx.lineTo(x + w - ix, y + h);
  ctx.lineTo(x + ix,     y + h);
  ctx.lineTo(x,          y + h - iy);
  ctx.lineTo(x,          y + iy);
  ctx.closePath();
  ctx.fill();
}

function measureLine(
  ctx: CanvasRenderingContext2D,
  font: string,
  text: string,
): number {
  ctx.font = font;
  return ctx.measureText(text).width;
}

// ── Renderer ───────────────────────────────────────────────────────────

/** Render a ShareGrid to a PNG Blob. Uses a transient canvas; never
    touches the DOM. The blob is `image/png` regardless of mode.
    Throws if `canvas.toBlob` returns null (extremely rare — happens on
    canvases too large to encode, which we don't approach in practice). */
export async function renderShareImageBlob(grid: ShareGrid): Promise<Blob> {
  // Pre-measure with a throwaway context so we can size the content
  // before deciding on a scale factor.
  const measureCanvas = document.createElement("canvas");
  const measureCtx = measureCanvas.getContext("2d")!;
  const widestRow = grid.rows.reduce((w, r) => Math.max(w, rowWidth(r)), 0);
  const headerW = measureLine(measureCtx, HEADER_FONT, grid.header);
  const footerW = measureLine(measureCtx, FOOTER_FONT, grid.footer);
  // Solve-badge below the footer: nothing on a loss; "SOLVED!" if the
  // player won with help; the hard-mode flourish if they won clean.
  const badgeText: string | null =
    grid.solveBadge === "hard"
      ? "🐲 HARD MODE SOLVE! 🐲"
      : grid.solveBadge === "solved"
        ? "SOLVED!"
        : null;
  const badgeW =
    badgeText != null ? measureLine(measureCtx, HARD_FONT, badgeText) : 0;
  const urlW = URL_LINES.reduce(
    (w, line) => Math.max(w, measureLine(measureCtx, URL_FONT, line)),
    0,
  );
  const contentW =
    Math.ceil(Math.max(widestRow, headerW, footerW, badgeW, urlW)) || 1;

  // Each row occupies one SLOT of vertical space (square grid cells); the
  // tile inside is centered both horizontally and vertically within its
  // slot, so the inter-row visible gap = SLOT − CELL = (1 − CELL_FILL)·SLOT.
  const rowsBlockH = grid.rows.length * SLOT;

  // Three independent vertical sections: header is pinned at MIN_MARGIN
  // from the top, URL is pinned at MIN_MARGIN from the bottom, and the
  // middle (rows + footer + optional solve-badge) is vertically centered
  // in the canvas. The scale is the largest factor that keeps every
  // section inside the drawable area without the centered middle
  // overlapping either pinned section (a SECTION_GAP cushion is honored
  // on each side).
  const headerH = HEADER_LH;
  const middleH =
    rowsBlockH +
    SECTION_GAP +
    FOOTER_LH +
    (badgeText != null ? SECTION_GAP / 2 + HARD_LH : 0);
  const urlH = URL_LH * URL_LINES.length;

  const halfAvail = FIXED_SIZE / 2 - MIN_MARGIN;
  const topReach = headerH + SECTION_GAP + middleH / 2;
  const botReach = urlH + SECTION_GAP + middleH / 2;
  const scale = Math.min(
    MAX_CONTENT / contentW,
    halfAvail / topReach,
    halfAvail / botReach,
  );

  // Kick off the monogram fetch in parallel with measurement; we await
  // it just before painting the background.
  const monogramReady = loadMonogram().catch(() => null);

  const dpr = Math.max(1, Math.min(3, window.devicePixelRatio || 1));
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(FIXED_SIZE * dpr);
  canvas.height = Math.ceil(FIXED_SIZE * dpr);
  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  // Background — monogram filled edge-to-edge, then darkened with a 70%
  // black overlay so the light foreground stands out (the gold center of
  // the monogram is otherwise too bright to read white text against). If
  // the fetch failed, fall back to a flat dark fill in the same color
  // family so the light foreground stays legible.
  const monogram = await monogramReady;
  if (monogram != null) {
    ctx.drawImage(monogram, 0, 0, FIXED_SIZE, FIXED_SIZE);
    ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
    ctx.fillRect(0, 0, FIXED_SIZE, FIXED_SIZE);
  } else {
    ctx.fillStyle = MONOGRAM_FALLBACK_BG;
    ctx.fillRect(0, 0, FIXED_SIZE, FIXED_SIZE);
  }

  // ── Header — pinned to top margin, top-anchored.
  ctx.save();
  ctx.translate(FIXED_SIZE / 2, MIN_MARGIN);
  ctx.scale(scale, scale);
  ctx.fillStyle = FG;
  ctx.font = HEADER_FONT;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(grid.header, 0, 0);
  ctx.restore();

  // ── Middle (rows + footer + optional hard-mode line) — vertically
  //    centered in the canvas. Tile cells walk by SLOT and the visible
  //    tile is inset TILE_INSET on each side so it ends up centered in
  //    its slot. Spaces and punctuation keep their inline widths and
  //    don't follow the grid.
  ctx.save();
  ctx.translate(FIXED_SIZE / 2, FIXED_SIZE / 2);
  ctx.scale(scale, scale);
  ctx.translate(0, -middleH / 2);
  let y = 0;
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  for (const row of grid.rows) {
    const w = rowWidth(row);
    let x = -w / 2;
    for (let i = 0; i < row.length; i++) {
      const c = row[i]!;
      if (c.kind === "square") {
        ctx.fillStyle = PALETTE[c.mark];
        ctx.globalAlpha = CELL_ALPHA;
        if (c.mark === "red") {
          octagon(ctx, x + TILE_INSET, y + TILE_INSET, CELL, CELL);
        } else {
          roundRect(ctx, x + TILE_INSET, y + TILE_INSET, CELL, CELL, CELL_RADIUS);
        }
        ctx.globalAlpha = 1;
        x += SLOT;
      } else if (c.kind === "space") {
        x += SPACE_W;
      } else {
        ctx.fillStyle = FG;
        ctx.font = PUNCT_FONT;
        ctx.fillText(c.ch, x + PUNCT_W / 2, y + SLOT / 2 + 1);
        x += PUNCT_W;
      }
    }
    y += SLOT;
  }
  y += SECTION_GAP;

  ctx.fillStyle = FG;
  ctx.font = FOOTER_FONT;
  ctx.textBaseline = "top";
  ctx.fillText(grid.footer, 0, y);
  y += FOOTER_LH;

  if (badgeText != null) {
    y += SECTION_GAP / 2;
    ctx.font = HARD_FONT;
    ctx.fillText(badgeText, 0, y);
  }
  ctx.restore();

  // ── URL block — two centered lines pinned to the bottom margin. The
  //    last line (the canonical URL) sits flush with the margin; lines
  //    above it stack upward by URL_LH each.
  ctx.save();
  ctx.translate(FIXED_SIZE / 2, FIXED_SIZE - MIN_MARGIN);
  ctx.scale(scale, scale);
  ctx.fillStyle = FG;
  ctx.font = URL_FONT;
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";
  for (let i = 0; i < URL_LINES.length; i++) {
    const linesFromBottom = URL_LINES.length - 1 - i;
    ctx.fillText(URL_LINES[i]!, 0, -linesFromBottom * URL_LH);
  }
  ctx.restore();

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob != null) resolve(blob);
      else reject(new Error("canvas.toBlob returned null"));
    }, "image/png");
  });
}
